import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Checkbox, Paper, Typography } from '@mui/material';
import i18n from '../../i18n';
import type { MenuDish, SecondMenu } from '../../models';
import {
    createMenuSubcategoryDishRowKey,
    type MenuLibraryManagementState,
} from './menu-library-management-state';
import {
    CreateMenuSubcategoryDraftRow,
    type SubcategoryDraftFieldKey,
} from './menu-library-create-subcategory-draft-row.component';
import { ReactComponent as AddIcon } from '../../static/images/svg/mingcute_add-fill.svg';
/** solar:copy-bold（批量复制菜品） */
import { ReactComponent as CopyBoldIcon } from '../../static/images/svg/solar_copy-bold.svg';
/** ic:baseline-delete-forever（批量删除菜品） */
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
/** gg:arrows-exchange（批量转移菜品） */
import { ReactComponent as GgArrowsExchangeIcon } from '../../static/images/svg/gg_arrows-exchange.svg';
import './scss/menu-library-create-menu-dialog.style.scss';
import './scss/menu-library-menu-detail.style.scss';
import {
    MenuLibrarySubcategoryPickerDialog,
    type MenuLibrarySubcategoryPickerMode,
} from './menu-library-subcategory-picker-dialog.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';

export type MenuLibraryCreateSubcategoryDetailPanelProps = {
    /** 菜单库状态（新建弹窗字段与草稿） */
    state: MenuLibraryManagementState;
    /** 与列表共用：子分类行内联编辑态（详情顶栏摘要同样可编辑） */
    subcategoryEdit: { tempId: string; field: SubcategoryDraftFieldKey } | null;
    setSubcategoryEdit: React.Dispatch<
        React.SetStateAction<{ tempId: string; field: SubcategoryDraftFieldKey } | null>
    >;
};

/**
 * 菜品表列标题 i18n key（顺序与 CSS 网格列一致；首列为勾选，不在此数组）
 */
const DISH_TABLE_HEAD_I18N_KEYS = [
    'menu_library_create_dialog_subcategory_detail_dish_col_name1',
    'menu_library_create_dialog_subcategory_detail_dish_col_dine_in',
    'menu_library_create_dialog_subcategory_detail_dish_col_takeout',
    'menu_library_create_dialog_subcategory_detail_dish_col_print',
    'menu_library_create_dialog_subcategory_detail_dish_col_mnemonic',
    'menu_library_create_dialog_subcategory_detail_dish_col_item_limit',
    'menu_library_create_dialog_subcategory_detail_dish_col_type',
    'menu_library_create_dialog_subcategory_detail_dish_col_detail',
] as const;

/**
 * 子分类详情内菜品表数据行
 */
function CreateMenuSubcategoryDishTableRow(props: {
    state: MenuLibraryManagementState;
    dish: MenuDish;
    rowKey: string;
    checked: boolean;
    onCheckedChange: (next: boolean) => void;
    onViewDetail: () => void;
}) {
    const { state, dish, rowKey, checked, onCheckedChange, onViewDetail } = props;
    const dineIn =
        dish.price_1 !== undefined && dish.price_1 !== null
            ? `${state.getModuleCurrencyName()} ${dish.price_1}`
            : '—';
    const takeout =
        dish.takeout_price_1 !== undefined && dish.takeout_price_1 !== null
            ? `${state.getModuleCurrencyName()} ${dish.takeout_price_1}`
            : '—';
    const limitText =
        dish.rule_lnr_max_num !== undefined && dish.rule_lnr_max_num !== null
            ? String(dish.rule_lnr_max_num)
            : '—';

    const name1Text = (dish.item_name1 ?? '').trim() || '—';

    const dishTableDataCells: { text: string; title?: string }[] = [
        { text: name1Text, title: dish.item_name1 || undefined },
        { text: dineIn },
        { text: takeout },
        { text: state.getPrintClassLabel(dish.print_class) },
        { text: dish.nlu ?? '—' },
        { text: limitText },
        { text: state.getDishTypeLabel(dish.item_type) },
    ];

    return (
        <Box
            className="menu-library-create-dialog__dish-table-row menu-library-create-dialog__dish-table-row--data"
            role="row"
            data-row-key={rowKey}
        >
            <Box className="menu-library-create-dialog__dish-cell menu-library-create-dialog__dish-cell--check">
                <Checkbox
                    className="menu-library-create-dialog__dish-row-checkbox"
                    checked={checked}
                    onChange={(_, v) => onCheckedChange(v)}
                    inputProps={{ 'aria-label': i18n.t('menu_library_create_dialog_subcategory_detail_select_row') }}
                    size="medium"
                />
            </Box>
            {dishTableDataCells.map((cell, index) => (
                <Typography
                    key={DISH_TABLE_HEAD_I18N_KEYS[index]}
                    className="menu-library-create-dialog__dish-cell-text"
                    noWrap
                    title={cell.title}
                >
                    {cell.text}
                </Typography>
            ))}
            <Box className="menu-library-create-dialog__dish-cell menu-library-create-dialog__dish-cell--detail">
                <Button
                    type="button"
                    variant="text"
                    size="small"
                    className="menu-library-create-dialog__dish-view-detail-btn"
                    onClick={onViewDetail}
                >
                    {i18n.t('menu_library_create_dialog_subcategory_detail_dish_view_detail')}
                </Button>
            </Box>
        </Box>
    );
}

/**
 * 新建弹窗 — 子分类详情：顶栏摘要、虚线分隔、可滚动菜品表、底部「新增菜品」或勾选后的批量操作条
 */
export const CreateMenuSubcategoryDetailPanel: React.FC<MenuLibraryCreateSubcategoryDetailPanelProps> = observer(
    ({ state, subcategoryEdit, setSubcategoryEdit }) => {
        const tempId = state.createMenuSubcategoryDetailTempId;
        const row = state.createMenuSubcategoryDrafts.find((r) => r.tempId === tempId);

        const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
        const [subcategoryPickerMode, setSubcategoryPickerMode] = useState<MenuLibrarySubcategoryPickerMode | null>(null);
        const [subcategoryPickerConfirmLoading, setSubcategoryPickerConfirmLoading] = useState(false);
        const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);

        const detailDishes = state.getCreateMenuSubcategoryDetailDishes();

        const rowKeys = useMemo(
            () => detailDishes.map((d, i) => createMenuSubcategoryDishRowKey(d, i)),
            [detailDishes],
        );

        useEffect(() => {
            const valid = new Set(rowKeys);
            setSelectedKeys((prev) => new Set([...prev].filter((k) => valid.has(k))));
        }, [rowKeys]);

        useEffect(() => {
            if (tempId && !row) {
                state.closeCreateMenuSubcategoryDetail();
            }
        }, [tempId, row]);

        const allSelected = rowKeys.length > 0 && rowKeys.every((k) => selectedKeys.has(k));
        const someSelected = rowKeys.some((k) => selectedKeys.has(k));

        const toggleRow = useCallback((key: string, next: boolean) => {
            setSelectedKeys((prev) => {
                const n = new Set(prev);
                if (next) {
                    n.add(key);
                } else {
                    n.delete(key);
                }
                return n;
            });
        }, []);

        const toggleSelectAll = useCallback(() => {
            if (allSelected) {
                setSelectedKeys(new Set());
            } else {
                setSelectedKeys(new Set(rowKeys));
            }
        }, [allSelected, rowKeys]);

        const openSubcategoryPicker = useCallback(
            (mode: MenuLibrarySubcategoryPickerMode) => {
                void state.ensureCourseGroupPickerMenusLoaded();
                setSubcategoryPickerMode(mode);
            },
            [state],
        );

        const closeSubcategoryPicker = useCallback(() => {
            setSubcategoryPickerMode(null);
            setSubcategoryPickerConfirmLoading(false);
        }, []);

        const handleSubcategoryPickerConfirm = useCallback(
            async (targetSecondMenu: SecondMenu) => {
                if (!subcategoryPickerMode || selectedKeys.size === 0) {
                    return;
                }
                const keys = [...selectedKeys];
                setSubcategoryPickerConfirmLoading(true);
                try {
                    const ok =
                        subcategoryPickerMode === 'transfer'
                            ? await state.batchTransferCreateMenuSubcategoryDishesByRowKeys(keys, targetSecondMenu)
                            : await state.batchCopyCreateMenuSubcategoryDishesByRowKeys(keys, targetSecondMenu);
                    if (ok) {
                        closeSubcategoryPicker();
                        setSelectedKeys(new Set());
                    }
                } finally {
                    setSubcategoryPickerConfirmLoading(false);
                }
            },
            [subcategoryPickerMode, selectedKeys, state, closeSubcategoryPicker],
        );

        const handleBatchDelete = useCallback(async () => {
            if (selectedKeys.size === 0) {
                return;
            }
            await state.deleteCreateMenuSubcategoryDishesByRowKeys([...selectedKeys]);
            setSelectedKeys(new Set());
        }, [selectedKeys, state]);

        if (!row) {
            return null;
        }

        const hasSelection = selectedKeys.size > 0;

        return (
            <Box className="menu-library-create-dialog menu-library-menu-detail menu-library-create-dialog--sub-detail">
                <Box className="menu-library-create-dialog__sub-detail-wrap">
                    <Paper variant="outlined" className="menu-library-create-dialog__sub-detail-summary" elevation={0}>
                        <CreateMenuSubcategoryDraftRow
                            variant="detail"
                            state={state}
                            row={row}
                            subcategoryEdit={subcategoryEdit}
                            setSubcategoryEdit={setSubcategoryEdit}
                        />
                    </Paper>
                    <Box className="menu-library-create-dialog__sub-detail-divider" aria-hidden />
                    <Box className="menu-library-create-dialog__sub-detail-scroll">
                        <Box className="menu-library-create-dialog__sub-detail-scroll-inner">
                            {detailDishes.length > 0 ? (
                                <Box className="menu-library-create-dialog__dish-table">
                                    <Box
                                        className="menu-library-create-dialog__dish-table-row menu-library-create-dialog__dish-table-row--head"
                                        role="row"
                                    >
                                        <Box className="menu-library-create-dialog__dish-cell menu-library-create-dialog__dish-cell--check">
                                            <Checkbox
                                                className="menu-library-create-dialog__dish-row-checkbox"
                                                indeterminate={someSelected && !allSelected}
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                inputProps={{
                                                    'aria-label': i18n.t(
                                                        'menu_library_create_dialog_subcategory_detail_select_all',
                                                    ),
                                                }}
                                                size="medium"
                                            />
                                        </Box>
                                        {DISH_TABLE_HEAD_I18N_KEYS.map((labelKey) => (
                                            <Typography
                                                key={labelKey}
                                                className="menu-library-create-dialog__dish-head-text"
                                                component="span"
                                                role="columnheader"
                                            >
                                                {i18n.t(labelKey)}
                                            </Typography>
                                        ))}
                                    </Box>
                                    {detailDishes.map((dish, index) => {
                                        const rk = createMenuSubcategoryDishRowKey(dish, index);
                                        return (
                                            <CreateMenuSubcategoryDishTableRow
                                                key={rk}
                                                state={state}
                                                dish={dish}
                                                rowKey={rk}
                                                checked={selectedKeys.has(rk)}
                                                onCheckedChange={(v) => toggleRow(rk, v)}
                                                onViewDetail={() => state.openCreateMenuSubcategoryAddDishDialogForEdit(rk)}
                                            />
                                        );
                                    })}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                    {hasSelection ? (
                        <>
                            <Paper
                                elevation={0}
                                variant="outlined"
                                className="menu-library-create-dialog__dish-batch-bar"
                                sx={{ flexShrink: 0, mt: 1 }}
                            >
                                <Button
                                    type="button"
                                    variant="text"
                                    className="menu-library-create-dialog__dish-batch-btn menu-library-create-dialog__dish-batch-btn--settings"
                                    onClick={() => state.openBatchDishSettingsDialog([...selectedKeys])}
                                >
                                    {i18n.t('menu_library_create_dialog_batch_dish_settings')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="text"
                                    className="menu-library-create-dialog__dish-batch-btn"
                                    startIcon={
                                        <GgArrowsExchangeIcon
                                            className="menu-library-create-dialog__dish-batch-exchange-icon"
                                            aria-hidden
                                        />
                                    }
                                    onClick={() => openSubcategoryPicker('transfer')}
                                >
                                    {i18n.t('menu_library_create_dialog_batch_transfer_dishes')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="text"
                                    className="menu-library-create-dialog__dish-batch-btn"
                                    startIcon={<CopyBoldIcon aria-hidden />}
                                    onClick={() => openSubcategoryPicker('copy')}
                                >
                                    {i18n.t('menu_library_create_dialog_batch_copy_dishes')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="text"
                                    className="menu-library-create-dialog__dish-batch-btn menu-library-create-dialog__dish-batch-btn--danger"
                                    startIcon={
                                        <BaselineDeleteForeverIcon
                                            className="menu-library-create-dialog__sub-delete-icon"
                                            aria-hidden
                                        />
                                    }
                                    disabled={state.isSubmittingCreate}
                                    onClick={() => setBatchDeleteConfirmOpen(true)}
                                >
                                    {i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                </Button>
                            </Paper>
                            <AlertDialog
                                open={batchDeleteConfirmOpen}
                                variant="delete"
                                size="sm"
                                title={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                                highlightText={i18n.t(
                                    'menu_library_create_dialog_batch_delete_confirm_highlight',
                                    { count: selectedKeys.size },
                                )}
                                confirmText={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                onCancel={() => setBatchDeleteConfirmOpen(false)}
                                onConfirm={() => {
                                    setBatchDeleteConfirmOpen(false);
                                    void handleBatchDelete();
                                }}
                            />
                            <MenuLibrarySubcategoryPickerDialog
                                open={subcategoryPickerMode != null}
                                mode={subcategoryPickerMode}
                                onClose={closeSubcategoryPicker}
                                menuData={state.courseGroupPickerMenusAll ?? []}
                                loading={state.courseGroupPickerMenusLoading}
                                excludeSluIds={row.dmi_slu_id != null ? [row.dmi_slu_id] : []}
                                confirmLoading={subcategoryPickerConfirmLoading}
                                onConfirm={handleSubcategoryPickerConfirm}
                            />
                        </>
                    ) : (
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            fullWidth
                            className="menu-library-create-dialog__add-sub menu-library-create-dialog__add-sub--bar menu-library-create-dialog__sub-wrap-footer"
                            sx={{ flexShrink: 0, mt: 1 }}
                            disabled={state.isSubmittingCreate}
                            onClick={() => state.openCreateMenuSubcategoryAddDishDialog()}
                        >
                            <span className="menu-library-create-dialog__add-sub-inner">
                                <Typography component="span" className="menu-library-create-dialog__add-sub-label">
                                    {i18n.t('menu_library_create_dialog_add_dish')}
                                </Typography>
                                <AddIcon className="menu-library-create-dialog__add-sub-icon" aria-hidden />
                            </span>
                        </Button>
                    )}
                </Box>
            </Box>
        );
    },
);
